import type { Ref } from 'vue';
import { computed } from 'vue';

import type { ICommissionCountryFilterItem } from '@modules/affiliate-program-commissions/submodules/edit/types';
import type { ICountry } from '@modules/countries/types';
import type { IFormPlainDataListboxItem } from '@modules/forms/types';

export interface CommissionCountriesSelectorComposable {
  countriesOptions: Ref<IFormPlainDataListboxItem<number>[]>;
  onChangeFilterName(value?: string): void;
  onChangeValue(value?: number[]): void;
}

export default function useCommissionCountriesSelector(
  countries: Ref<ICountry[] | undefined>,
  activeCountryFilter: Ref<ICommissionCountryFilterItem | undefined>,
  activeFilterIndex: Ref<number>,
  countryFilters: Ref<ICommissionCountryFilterItem[]>,
): CommissionCountriesSelectorComposable {
  const countriesOptions = computed<IFormPlainDataListboxItem<number>[]>(() => {
    if (!countries.value) {
      return [];
    }

    const selectedCountries = countryFilters.value.filter((item, index) => activeFilterIndex.value !== index && !item._destroy).flatMap((item) => item.value);

    return countries.value.filter((item) => !selectedCountries.includes(item.country_code)).map((item) => ({
      id: item.country_code,
      value: item.name,
      check: activeCountryFilter.value?.value.includes(item.country_code),
    }));
  });

  function onChangeFilterName(value?: string): void {
    const activeFilter = countryFilters.value[activeFilterIndex.value];
    if (activeFilter) {
      countryFilters.value[activeFilterIndex.value] = {
        ...activeFilter,
        label: value,
      };
    }
  }

  function onChangeValue(value?: number[]): void {
    const activeFilter = countryFilters.value[activeFilterIndex.value];
    if (activeFilter) {
      countryFilters.value[activeFilterIndex.value] = {
        ...activeFilter,
        value: value ?? [],
      };
    }
  }

  return {
    countriesOptions,
    onChangeFilterName,
    onChangeValue,
  };
}
